<template>
  <TransitionRoot appear :show="true" as="template">
    <Dialog as="div" class="relative z-10">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="
                w-full
                max-w-xl
                transform
                overflow-hidden
                rounded-xl
                bg-white
                text-left
                align-middle
                shadow-xl
                transition-all
                p-4
              "
            >
              <DialogOverlay class="p-5 pt-5">
                <div class="col-span-2 block mb-4">
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300" />
                    </div>
                    <div class="relative flex justify-between">
                      <h2 class="font-bold pr-3 bg-white col-md-6 text-xl text-vice-teal-900">
                        Import students
                      </h2>
                      <span class="p-1 pl-3 bg-white cursor-pointer" @click="close">
                        <CrossGrayIcon class="h-6 w-6" />
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="!result" class="flex border-b border-gray-200 mb-4 text-left">
                  <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium -mb-px border-b-2"
                    :class="
                      activeTab === 'import'
                        ? 'border-vice-teal-500 text-vice-teal-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    "
                    @click="switchTab('import')"
                  >
                    Import students
                  </button>
                  <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium -mb-px border-b-2"
                    :class="
                      activeTab === 'facilitator'
                        ? 'border-vice-teal-500 text-vice-teal-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    "
                    @click="switchTab('facilitator')"
                  >
                    Invite facilitator
                  </button>
                </div>

                <div
                  v-if="!result && activeTab === 'import'"
                  class="container max-w-full justify-between text-sm"
                >
                  <form @submit.prevent="submit" class="p-2 relative text-left">
                    <div>
                      <jet-label for="university_id" value="University *" />
                      <select
                        id="university_id"
                        v-model="form.university_id"
                        required
                        class="
                          mt-1
                          block
                          w-full
                          border-gray-300
                          focus:border-indigo-300
                          focus:ring focus:ring-indigo-200 focus:ring-opacity-50
                          rounded-md
                          shadow-sm
                          sm:text-sm
                        "
                        @change="loadGroups"
                      >
                        <option v-for="uni in universities" :key="uni.id" :value="uni.id">
                          {{ uni.name }}
                        </option>
                      </select>
                    </div>

                    <div class="mt-4">
                      <jet-label for="group_id" value="Group" />
                      <select
                        id="group_id"
                        v-model="form.group_id"
                        class="
                          mt-1
                          block
                          w-full
                          border-gray-300
                          focus:border-indigo-300
                          focus:ring focus:ring-indigo-200 focus:ring-opacity-50
                          rounded-md
                          shadow-sm
                          sm:text-sm
                        "
                      >
                        <option :value="null">-</option>
                        <option v-for="group in groups" :key="group.id" :value="group.id">
                          {{ group.name }}
                        </option>
                      </select>
                    </div>

                    <div class="mt-4">
                      <jet-label value="Students CSV *" />
                      <div
                        class="
                          mt-1
                          flex
                          justify-center
                          rounded-md
                          border-2 border-gray-300 border-dashed
                          px-6
                          py-8
                          cursor-pointer
                        "
                        :class="{ 'border-vice-teal-500 bg-vice-light-grey': isDragging }"
                        @click="$refs.csvInput.click()"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="onDrop"
                      >
                        <div class="text-center">
                          <p v-if="!file" class="text-sm text-gray-500">
                            Drag and drop a CSV file here, or click to browse.
                          </p>
                          <p v-else class="text-sm font-medium text-vice-teal-900">
                            {{ file.name }}
                            <span v-if="rowCount !== null" class="text-gray-500">
                              ({{ rowCount }} row{{ rowCount === 1 ? '' : 's' }})
                            </span>
                          </p>
                          <p class="text-xs text-gray-400 mt-1">
                            Requires "name" and "email" columns.
                          </p>
                        </div>
                      </div>
                      <input
                        ref="csvInput"
                        type="file"
                        accept=".csv,text/csv"
                        class="hidden"
                        @change="onFileSelected"
                      />
                    </div>

                    <div v-if="error" class="mt-3 text-sm text-red-600">
                      {{ error }}
                    </div>

                    <div class="flex mt-8 gap-3">
                      <jet-button
                        :class="{ 'opacity-60': uploading || !file || !form.university_id }"
                        :disabled="uploading || !file || !form.university_id"
                      >
                        {{ uploading ? 'Importing...' : 'Import students' }}
                      </jet-button>
                      <button
                        type="button"
                        @click="close"
                        class="
                          inline-flex
                          items-center
                          px-4
                          py-2
                          border border-gray-300
                          rounded-md
                          shadow-sm
                          text-sm
                          font-medium
                          text-gray-700
                          bg-white
                          hover:bg-gray-50
                          focus:outline-none
                          focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                        "
                      >
                        CANCEL
                      </button>
                    </div>
                  </form>
                </div>

                <div
                  v-else-if="!result && activeTab === 'facilitator'"
                  class="container max-w-full justify-between text-sm"
                >
                  <form @submit.prevent="submitFacilitator" class="p-2 relative text-left">
                    <div>
                      <jet-label for="facilitator_name" value="Name *" />
                      <jet-input
                        id="facilitator_name"
                        type="text"
                        class="mt-1 block w-full"
                        v-model="facilitatorForm.name"
                        required
                      />
                    </div>

                    <div class="mt-4">
                      <jet-label for="facilitator_email" value="Email *" />
                      <jet-input
                        id="facilitator_email"
                        type="email"
                        class="mt-1 block w-full"
                        v-model="facilitatorForm.email"
                        required
                      />
                    </div>

                    <div class="mt-4">
                      <jet-label for="facilitator_university_id" value="University *" />
                      <select
                        id="facilitator_university_id"
                        v-model="form.university_id"
                        required
                        class="
                          mt-1
                          block
                          w-full
                          border-gray-300
                          focus:border-indigo-300
                          focus:ring focus:ring-indigo-200 focus:ring-opacity-50
                          rounded-md
                          shadow-sm
                          sm:text-sm
                        "
                        @change="loadGroups"
                      >
                        <option v-for="uni in universities" :key="uni.id" :value="uni.id">
                          {{ uni.name }}
                        </option>
                      </select>
                    </div>

                    <div class="mt-4">
                      <jet-label for="facilitator_group_id" value="Group" />
                      <select
                        id="facilitator_group_id"
                        v-model="form.group_id"
                        class="
                          mt-1
                          block
                          w-full
                          border-gray-300
                          focus:border-indigo-300
                          focus:ring focus:ring-indigo-200 focus:ring-opacity-50
                          rounded-md
                          shadow-sm
                          sm:text-sm
                        "
                      >
                        <option :value="null">-</option>
                        <option v-for="group in groups" :key="group.id" :value="group.id">
                          {{ group.name }}
                        </option>
                      </select>
                    </div>

                    <div v-if="error" class="mt-3 text-sm text-red-600">
                      {{ error }}
                    </div>

                    <div class="flex mt-8 gap-3">
                      <jet-button
                        :class="{
                          'opacity-60':
                            uploading ||
                            !facilitatorForm.name ||
                            !facilitatorForm.email ||
                            !form.university_id,
                        }"
                        :disabled="
                          uploading ||
                          !facilitatorForm.name ||
                          !facilitatorForm.email ||
                          !form.university_id
                        "
                      >
                        {{ uploading ? 'Inviting...' : 'Invite facilitator' }}
                      </jet-button>
                      <button
                        type="button"
                        @click="close"
                        class="
                          inline-flex
                          items-center
                          px-4
                          py-2
                          border border-gray-300
                          rounded-md
                          shadow-sm
                          text-sm
                          font-medium
                          text-gray-700
                          bg-white
                          hover:bg-gray-50
                          focus:outline-none
                          focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                        "
                      >
                        CANCEL
                      </button>
                    </div>
                  </form>
                </div>

                <div v-else class="container max-w-full text-left text-sm p-2">
                  <p class="text-vice-teal-900 font-medium">
                    {{ result.created_count }} {{ resultLabel
                    }}{{ result.created_count === 1 ? '' : 's' }}
                    invited.
                  </p>
                  <p v-if="result.skipped_existing.length" class="mt-2 text-gray-600">
                    {{ result.skipped_existing.length }} already had an account and were skipped:
                    <span class="text-gray-500">{{ result.skipped_existing.join(', ') }}</span>
                  </p>
                  <p v-if="result.invalid_rows.length" class="mt-2 text-red-600">
                    {{ result.invalid_rows.length }} row{{
                      result.invalid_rows.length === 1 ? '' : 's'
                    }}
                    skipped:
                    <span v-for="row in result.invalid_rows" :key="row.row" class="block text-xs">
                      Row {{ row.row }} ({{ row.email }}): {{ row.reason }}
                    </span>
                  </p>
                  <div class="flex mt-6">
                    <jet-button @click="close">Done</jet-button>
                  </div>
                </div>
              </DialogOverlay>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  Dialog,
  DialogOverlay,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import Papa from 'papaparse';
import JetLabel from '@/Jetstream/Label.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetButton from '@/Jetstream/Button.vue';
import CrossGrayIcon from '@/Components/Icons/CrossGrayIcon.vue';

export default {
  name: 'ImportStudentsModal',
  props: {
    universities: {
      type: Array,
      required: true,
    },
    preselectedUniversityId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['close', 'imported'],
  components: {
    JetButton,
    JetLabel,
    JetInput,
    CrossGrayIcon,
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogOverlay,
  },

  data() {
    return {
      activeTab: 'import',
      form: {
        university_id: this.preselectedUniversityId,
        group_id: null,
      },
      facilitatorForm: {
        name: '',
        email: '',
      },
      groups: [],
      file: null,
      rowCount: null,
      isDragging: false,
      uploading: false,
      error: null,
      result: null,
    };
  },

  computed: {
    resultLabel() {
      return this.result?.created?.[0]?.role === 'facilitator' ? 'facilitator' : 'student';
    },
  },

  mounted() {
    if (this.form.university_id) {
      this.loadGroups();
    }
  },

  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      this.error = null;
    },

    async loadGroups() {
      this.form.group_id = null;
      this.groups = [];
      if (!this.form.university_id) return;
      try {
        const response = await axios.get(`/api/universities/${this.form.university_id}/groups`);
        this.groups = response.data;
      } catch (error) {
        console.error('Error loading groups:', error);
      }
    },

    onDrop(event) {
      this.isDragging = false;
      const droppedFile = event.dataTransfer.files[0];
      if (droppedFile) this.setFile(droppedFile);
    },

    onFileSelected(event) {
      const selected = event.target.files[0];
      if (selected) this.setFile(selected);
    },

    setFile(file) {
      this.file = file;
      this.error = null;
      this.rowCount = null;

      // Best-effort row count preview - the server does the authoritative parse/validation.
      Papa.parse(file, {
        delimiter: ',',
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          this.rowCount = results.data.length;
          console.log(results.data);
        },
        error: () => {
          this.rowCount = null;
        },
      });
    },

    async submit() {
      if (!this.file || !this.form.university_id) return;

      this.uploading = true;
      this.error = null;

      const data = new FormData();
      data.append('university_id', this.form.university_id);
      if (this.form.group_id) data.append('group_id', this.form.group_id);
      data.append('csv', this.file);

      try {
        const response = await axios.post(this.route('students.import'), data);
        this.result = response.data;
        this.$emit('imported', this.result);
      } catch (error) {
        this.error =
          error.response?.data?.error ??
          error.response?.data?.errors?.csv?.[0] ??
          'Something went wrong while importing students.';
      } finally {
        this.uploading = false;
      }
    },

    async submitFacilitator() {
      if (!this.facilitatorForm.name || !this.facilitatorForm.email || !this.form.university_id) {
        return;
      }

      this.uploading = true;
      this.error = null;

      try {
        const response = await axios.post(this.route('students.invite-facilitator'), {
          name: this.facilitatorForm.name,
          email: this.facilitatorForm.email,
          university_id: this.form.university_id,
          group_id: this.form.group_id,
        });
        this.result = response.data;
        this.$emit('imported', this.result);
      } catch (error) {
        this.error =
          error.response?.data?.error ??
          error.response?.data?.errors?.email?.[0] ??
          'Something went wrong while inviting the facilitator.';
      } finally {
        this.uploading = false;
      }
    },

    close() {
      this.$emit('close');
    },
  },
};
</script>
